/**
 * Helpers to save a file the API returns as a `Blob` (e.g. the dashboard usage report, generated as a PDF
 * on the server and requested with `responseType: "blob"`).
 */

import { translate } from "utils/utils";

/** Saves `blob` under `filename` through a temporary `<a download>` link. */
export const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");

	link.href = url;
	link.download = filename;
	link.rel = "noopener";
	link.style.display = "none";

	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);

	// Revoked later, not right away: Safari and Firefox may not have started reading the URL yet.
	setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * File name announced by a `Content-Disposition` header. The RFC 5987 `filename*=UTF-8''…` form wins (it
 * carries the non-ASCII name), then the plain `filename="…"`; `fallback` when the header is missing or has
 * neither. The API only exposes the header to the browser through `Access-Control-Expose-Headers`.
 */
export const filenameFromDisposition = (header: string | null | undefined, fallback: string): string => {
	if (!header || typeof header !== "string") return fallback;

	const extended = /filename\*\s*=\s*utf-8''([^;]+)/i.exec(header);
	if (extended) {
		try {
			const decoded = decodeURIComponent(extended[1].trim());
			if (decoded) return decoded;
		} catch (err) {
			// Malformed percent-encoding: fall back to the plain form below.
		}
	}

	const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
	const plain = quoted || /filename\s*=\s*([^;]+)/i.exec(header);
	const name = plain?.[1]?.trim();

	return name || fallback;
};

/**
 * `message` of the API's JSON envelope when it arrives as a `Blob`: with `responseType: "blob"` an error reply
 * is a Blob too, so `response.data.message` is not available and the body has to be read back. `results.message`
 * wins over the top-level `message`; either may be a plain string or a `{ pt, en, es }` object, resolved for the
 * current language by `translate`. Returns "" when `blob` is not a JSON body or cannot be parsed, so the caller
 * can fall back to its generic message.
 */
export const messageFromJsonBlob = async (blob: unknown): Promise<string> => {
	if (!(blob instanceof Blob) || !/^application\/json\b/i.test(blob.type)) return "";

	try {
		const envelope = JSON.parse(await blob.text());
		const message = translate(envelope?.results?.message ?? envelope?.message);

		return typeof message === "string" ? message : "";
	} catch (err) {
		// Not valid JSON (e.g. an HTML error page from a proxy): the caller shows its generic message.
		return "";
	}
};
